import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	Check,
	Paperclip,
	Plus,
	ScanLine,
	SlidersHorizontal,
	Tag,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ListFinancialTransactions200 } from "@/api/financial";
import {
	listFinancialCategoriesQueryOptions,
	listFinancialTransactionsQueryOptions,
	useReviewFinancialTransactions,
} from "@/api/financial";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatSignedMoney } from "@/utils/financial";
import {
	KindBadge,
	ReviewBadge,
	SourceBadge,
} from "../-components/FinancialBadges";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";

type TransactionRow = ListFinancialTransactions200["data"][number];

const ALL = "all";

const searchSchema = z.object({
	reviewState: z.string().optional(),
	kind: z.string().optional(),
	sourceModule: z.string().optional(),
	categoryId: z.string().optional(),
	search: z.string().optional(),
	farm: z.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/transactions/",
)({
	validateSearch: searchSchema,
	component: TransactionsListPage,
});

const KIND_OPTIONS = [
	"REVENUE",
	"DIRECT_COST",
	"OVERHEAD",
	"CAPITAL",
	"FINANCING",
] as const;
const REVIEW_OPTIONS = ["UNREVIEWED", "REVIEWED"] as const;
const SOURCE_OPTIONS = ["livestock", "grass", "equipment"] as const;

function TransactionsListPage() {
	const { orgId } = Route.useParams();
	const search = Route.useSearch();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const scope = useFarmScope();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [searchTerm, setSearchTerm] = useState(search.search ?? "");
	const [debouncedSearch, setDebouncedSearch] = useState(search.search ?? "");
	const [reviewState, setReviewState] = useState(search.reviewState ?? ALL);
	const [kind, setKind] = useState(search.kind ?? ALL);
	const [sourceModule, setSourceModule] = useState(search.sourceModule ?? ALL);
	const [categoryId, setCategoryId] = useState(search.categoryId ?? ALL);
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [bulkCategoryId, setBulkCategoryId] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setPage(1);
		}, 400);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const queryParams = useMemo(
		() => ({
			page: String(page),
			limit: "20",
			...(debouncedSearch ? { search: debouncedSearch } : {}),
			...(reviewState !== ALL ? { reviewState } : {}),
			...(kind !== ALL ? { kind } : {}),
			...(sourceModule !== ALL ? { sourceModule } : {}),
			...(categoryId !== ALL ? { categoryId } : {}),
			...(establishmentId ? { establishmentId } : {}),
		}),
		[
			page,
			debouncedSearch,
			reviewState,
			kind,
			sourceModule,
			categoryId,
			establishmentId,
		],
	);

	const { data, isLoading } = useQuery(
		listFinancialTransactionsQueryOptions(orgId, queryParams),
	);
	const { data: categories } = useQuery(
		listFinancialCategoriesQueryOptions(orgId),
	);
	const reviewMutation = useReviewFinancialTransactions();

	const rows = data?.data ?? [];
	const pagination = data?.pagination;

	const toggleSelected = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const reviewIds = (ids: string[], targetCategoryId?: string) =>
		reviewMutation.mutate(
			{
				organizationId: orgId,
				data: {
					transactionIds: ids,
					reviewState: "REVIEWED",
					...(targetCategoryId ? { categoryId: targetCategoryId } : {}),
				},
			},
			{
				onSuccess: () => {
					setSelected(new Set());
					setBulkCategoryId("");
				},
			},
		);

	const activeCategories = (categories ?? []).filter((c) => c.isActive);

	const activeFilterCount =
		(kind !== ALL ? 1 : 0) +
		(reviewState !== ALL ? 1 : 0) +
		(sourceModule !== ALL ? 1 : 0) +
		(categoryId !== ALL ? 1 : 0);

	const clearFilters = () => {
		setKind(ALL);
		setReviewState(ALL);
		setSourceModule(ALL);
		setCategoryId(ALL);
		setPage(1);
	};

	const columns: ColumnDef<TransactionRow>[] = [];
	if (canManage) {
		columns.push({
			id: "select",
			header: () => null,
			cell: ({ row }) => (
				// biome-ignore lint/a11y/noStaticElementInteractions: wrapper only stops row-click propagation; the Checkbox is the interactive control
				// biome-ignore lint/a11y/useKeyWithClickEvents: click guard only; the Checkbox handles keyboard interaction itself
				<div onClick={(e) => e.stopPropagation()} className="flex items-center">
					<Checkbox
						checked={selected.has(row.original.id)}
						onCheckedChange={() => toggleSelected(row.original.id)}
						aria-label="select-row"
					/>
				</div>
			),
			meta: { hiddenOnMobile: true },
		});
	}
	columns.push(
		{
			accessorKey: "eventDate",
			header: t("financial.transactions.columns.date"),
			cell: ({ row }) =>
				formatDateOnly(new Date(row.original.eventDate).getTime()),
		},
		{
			accessorKey: "description",
			header: t("financial.transactions.columns.description"),
			cell: ({ row }) => (
				<span className="flex items-center gap-1.5 font-medium">
					{row.original.sourceDescription ?? row.original.categoryLabel}
					{row.original.attachmentCount > 0 && (
						<Paperclip
							className="h-3.5 w-3.5 shrink-0 text-base-content/50"
							aria-label={t("financial.transactions.attachments.indicator")}
						/>
					)}
				</span>
			),
		},
		{
			accessorKey: "categoryLabel",
			header: t("financial.transactions.columns.category"),
			cell: ({ row }) => <KindBadge kind={row.original.kind} />,
			meta: { hiddenOnMobile: true },
		},
		{
			accessorKey: "sourceModule",
			header: t("financial.transactions.columns.source"),
			cell: ({ row }) => (
				<SourceBadge
					sourceModule={row.original.sourceModule}
					entryOrigin={row.original.entryOrigin}
				/>
			),
			meta: { hiddenOnMobile: true },
		},
		{
			accessorKey: "reviewState",
			header: t("financial.transactions.columns.review"),
			cell: ({ row }) => {
				if (row.original.reviewState === "REVIEWED") {
					return null;
				}
				return (
					<div className="flex items-center gap-2">
						<ReviewBadge reviewState={row.original.reviewState} />
						{canManage && (
							<Button
								size="sm"
								variant="ghost"
								onClick={(e) => {
									e.stopPropagation();
									reviewIds([row.original.id]);
								}}
								title={t("financial.transactions.review.markReviewed")}
							>
								<Check className="h-4 w-4" />
							</Button>
						)}
					</div>
				);
			},
		},
		{
			accessorKey: "amount",
			header: t("financial.transactions.columns.amount"),
			cell: ({ row }) => (
				<span
					className={
						row.original.status === "VOID"
							? "text-base-content/40 line-through"
							: row.original.direction === "OUT"
								? "font-medium text-error"
								: "font-medium text-success"
					}
				>
					{formatSignedMoney(
						row.original.amount,
						row.original.direction,
						row.original.currency,
					)}
				</span>
			),
		},
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-2">
					<Input
						type="search"
						placeholder={t("financial.transactions.filters.search")}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="flex-1 sm:w-64 sm:flex-none"
					/>
					<TransactionFilters
						activeCount={activeFilterCount}
						onClear={clearFilters}
						kind={kind}
						onKind={(v) => {
							setKind(v);
							setPage(1);
						}}
						reviewState={reviewState}
						onReviewState={(v) => {
							setReviewState(v);
							setPage(1);
						}}
						sourceModule={sourceModule}
						onSourceModule={(v) => {
							setSourceModule(v);
							setPage(1);
						}}
						categoryId={categoryId}
						onCategoryId={(v) => {
							setCategoryId(v);
							setPage(1);
						}}
						categoryOptions={activeCategories.map((c) => ({
							value: c.id,
							label: c.label,
						}))}
					/>
				</div>

				{canManage && (
					<FinancialHeaderActions>
						<HeaderActionButton
							variant="outline"
							icon={ScanLine}
							label={t("financial.receiptImport.action")}
							shortLabel={t("financial.receiptImport.actionShort")}
							onClick={() =>
								navigate({
									to: "/app/org/$orgId/financial/transactions/import",
									params: { orgId },
								})
							}
						/>
						<HeaderActionButton
							icon={Plus}
							label={t("financial.transactions.new")}
							shortLabel={t("financial.transactions.newShort")}
							onClick={() =>
								navigate({
									to: "/app/org/$orgId/financial/transactions/new",
									params: { orgId },
								})
							}
						/>
					</FinancialHeaderActions>
				)}
			</div>

			{canManage && selected.size > 0 && (
				<div className="flex flex-col gap-3 rounded-lg border border-base-300 bg-base-100 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
					<span className="text-sm">
						{t("financial.transactions.review.selected", {
							count: selected.size,
						})}
					</span>
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
						<Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
							<SelectTrigger className="sm:w-56">
								<SelectValue
									placeholder={t(
										"financial.transactions.review.bulkCategoryPlaceholder",
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{activeCategories.map((category) => (
									<SelectItem key={category.id} value={category.id}>
										{category.label} · {t(`financial.kinds.${category.kind}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Button
							size="sm"
							variant="outline"
							onClick={() => reviewIds(Array.from(selected), bulkCategoryId)}
							disabled={!bulkCategoryId || reviewMutation.isPending}
						>
							<Tag className="mr-2 h-4 w-4" />
							{t("financial.transactions.review.bulkCategorize")}
						</Button>
						<Button
							size="sm"
							onClick={() => reviewIds(Array.from(selected))}
							disabled={reviewMutation.isPending}
						>
							<Check className="mr-2 h-4 w-4" />
							{t("financial.transactions.review.bulkReview")}
						</Button>
					</div>
				</div>
			)}

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					<DataTable
						columns={columns}
						data={rows}
						isLoading={isLoading}
						canManage={canManage}
						onRowClick={(row) =>
							navigate({
								to: "/app/org/$orgId/financial/transactions/$transactionId",
								params: { orgId, transactionId: row.id },
							})
						}
						emptyState={{
							title: t("financial.transactions.empty.title"),
							description: t("financial.transactions.empty.description"),
							// A first-time user arriving from the setup guide must find
							// the same primary action here, not only in the page header
							// (plan §17 Phase 4 step 4).
							createButton: {
								label: t("financial.transactions.new"),
								href: `/app/org/${orgId}/financial/transactions/new`,
							},
						}}
						renderMobileCard={(item) => (
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="flex items-start gap-1.5 font-semibold">
										<span className="line-clamp-2 break-words">
											{item.sourceDescription ?? item.categoryLabel}
										</span>
										{item.attachmentCount > 0 && (
											<Paperclip
												className="h-3.5 w-3.5 shrink-0 text-base-content/50"
												aria-label={t(
													"financial.transactions.attachments.indicator",
												)}
											/>
										)}
									</p>
									<p className="mt-0.5 text-sm text-base-content/70">
										{formatDateOnly(new Date(item.eventDate).getTime())} ·{" "}
										{item.categoryLabel}
									</p>
									<div className="mt-1 flex gap-1">
										<SourceBadge
											sourceModule={item.sourceModule}
											entryOrigin={item.entryOrigin}
										/>
										<ReviewBadge reviewState={item.reviewState} />
									</div>
								</div>
								<span
									className={
										item.direction === "OUT"
											? "shrink-0 font-medium text-error"
											: "shrink-0 font-medium text-success"
									}
								>
									{formatSignedMoney(
										item.amount,
										item.direction,
										item.currency,
									)}
								</span>
							</div>
						)}
					/>
				</div>
			</div>

			{pagination && pagination.totalPages > 1 && (
				<div className="flex items-center justify-center gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={!pagination.hasPreviousPage}
						onClick={() => setPage((p) => Math.max(1, p - 1))}
					>
						{"<"}
					</Button>
					<Badge variant="outline">
						{pagination.page} / {pagination.totalPages}
					</Badge>
					<Button
						variant="outline"
						size="sm"
						disabled={!pagination.hasNextPage}
						onClick={() => setPage((p) => p + 1)}
					>
						{">"}
					</Button>
				</div>
			)}
		</div>
	);
}

interface FilterOption {
	value: string;
	label: string;
}

interface TransactionFiltersProps {
	activeCount: number;
	onClear: () => void;
	kind: string;
	onKind: (value: string) => void;
	reviewState: string;
	onReviewState: (value: string) => void;
	sourceModule: string;
	onSourceModule: (value: string) => void;
	categoryId: string;
	onCategoryId: (value: string) => void;
	categoryOptions: FilterOption[];
}

function TransactionFilters({
	activeCount,
	onClear,
	kind,
	onKind,
	reviewState,
	onReviewState,
	sourceModule,
	onSourceModule,
	categoryId,
	onCategoryId,
	categoryOptions,
}: TransactionFiltersProps) {
	const { t } = useTranslation();
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="outline">
					<SlidersHorizontal className="mr-2 h-4 w-4" />
					{t("financial.transactions.filters.button")}
					{activeCount > 0 && (
						<span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-content">
							{activeCount}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80">
				<div className="mb-3 flex items-center justify-between">
					<p className="text-sm font-semibold">
						{t("financial.transactions.filters.title")}
					</p>
					{activeCount > 0 && (
						<Button
							variant="ghost"
							size="sm"
							className="h-auto px-2 py-1 text-xs"
							onClick={onClear}
						>
							{t("financial.transactions.filters.clear")}
						</Button>
					)}
				</div>
				<div className="flex flex-col gap-3">
					<FilterSelect
						value={kind}
						onChange={onKind}
						label={t("financial.transactions.filters.kind")}
						options={KIND_OPTIONS.map((k) => ({
							value: k,
							label: t(`financial.kinds.${k}`),
						}))}
					/>
					<FilterSelect
						value={reviewState}
						onChange={onReviewState}
						label={t("financial.transactions.filters.reviewState")}
						options={REVIEW_OPTIONS.map((r) => ({
							value: r,
							label: t(`financial.reviewStates.${r}`),
						}))}
					/>
					<FilterSelect
						value={sourceModule}
						onChange={onSourceModule}
						label={t("financial.transactions.filters.sourceModule")}
						options={SOURCE_OPTIONS.map((s) => ({
							value: s,
							label: t(`financial.sourceModules.${s}`),
						}))}
					/>
					<FilterSelect
						value={categoryId}
						onChange={onCategoryId}
						label={t("financial.transactions.filters.category")}
						options={categoryOptions}
					/>
				</div>
			</PopoverContent>
		</Popover>
	);
}

interface FilterSelectProps {
	value: string;
	onChange: (value: string) => void;
	label: string;
	options: FilterOption[];
}

function FilterSelect({ value, onChange, label, options }: FilterSelectProps) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col gap-1.5">
			<span className="text-xs font-medium text-base-content/70">{label}</span>
			<Select value={value} onValueChange={onChange}>
				<SelectTrigger className="w-full">
					<SelectValue placeholder={label} />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={ALL}>
						{t("financial.transactions.filters.all")}
					</SelectItem>
					{options.map((option) => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}
